import type { FC } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

import settings from 'src/settings';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { useUserContext } from 'src/components/app/context/UserContext';
import PageFrame from 'src/components/common/PageFrame';
import Header from 'src/components/app/Header';
import { SurveyListProvider } from 'src/components/survey/SurveyContext/ListContext';
import BackButton from 'src/components/survey/SurveySelectionPage/components/BackButton';

import SurveySelectionTable from './components/SurveyTableView';
import SurveyWidgetView from './components/SurveyWidgetView';

import { styles } from 'src/components/survey/SurveySelectionPage/index.styles';

interface SurveySelectionPageProps {}

type ViewMode = 'table' | 'widget';

export const SurveySelectionPage: FC<SurveySelectionPageProps> = () => {
  const { t } = useTranslation(['survey', 'navigation']);
  const { currentSurvey, previousSurvey, unsetSurvey } = useUserContext();

  const widgetViewEnabled =
    settings.VITE_APP_IS_SURVEY_TABLE_VIEW_UNLOCKED === 'true';
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>(
    'surveyListView',
    widgetViewEnabled ? 'widget' : 'table',
  );

  // When the page is loaded, unset the survey so that components can know we
  //  are not currently in one.
  // Widgets of global dashboards, for example, need this to send API calls to
  //  the correct survey, which may be the global one.
  useEffect(() => {
    if (currentSurvey) {
      unsetSurvey();
    }
  }, [currentSurvey, unsetSurvey]);

  return (
    <PageFrame
      header={
        <Header hideProjectLogo hidePreviousVersionBanner hideSurveySettings />
      }
    >
      <Box>
        {!!previousSurvey && <BackButton />}
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            pb: 2,
          }}
        >
          <Stack
            direction="column"
            sx={{
              justifyContent: 'center',
            }}
          >
            <Typography sx={styles.headline}>
              {t('pageHeader.selectYourSurvey')}
            </Typography>
          </Stack>
          {widgetViewEnabled && (
            <FormControlLabel
              control={
                <Switch
                  checked={viewMode === 'table'}
                  onChange={(event) =>
                    setViewMode(event.target.checked ? 'table' : 'widget')
                  }
                />
              }
              label={t('selection.view')}
              labelPlacement="start"
            />
          )}
        </Stack>
      </Box>
      <Box
        sx={{
          pb: 4,
        }}
      >
        <SurveyListProvider>
          {viewMode === 'table' || !widgetViewEnabled ? (
            <SurveySelectionTable />
          ) : (
            <SurveyWidgetView />
          )}
        </SurveyListProvider>
      </Box>
    </PageFrame>
  );
};

export default SurveySelectionPage;
